/**
 * お問い合わせ関連のユーティリティ関数
 */

/**
 * 一意の識別子を生成する（UUIDの最初の8文字を使用）
 * @returns {string} 8文字の大文字識別子
 */
export function generateContactId(): string {
  try {
    // UUID v4を生成（モダンブラウザ対応）
    // NOTE: crypto.randomUUIDは比較的新しいWeb APIのため、
    // 古いTypeScript型定義では認識されないが、実際のモダンブラウザでは利用可能
    // そのため (crypto as any) でキャストして型チェックを回避
    if (typeof crypto !== "undefined" && (crypto as any).randomUUID) {
      const uuid = (crypto as any).randomUUID();
      return uuid.substring(0, 8).toUpperCase();
    }

    // フォールバック: 手動でUUID風の文字列を生成
    return generateFallbackId();
  } catch (error) {
    console.warn("UUID generation failed, using fallback method:", error);
    return generateFallbackId();
  }
}

/**
 * フォールバック用の識別子生成（古いブラウザ対応）
 * @returns {string} 8文字の大文字識別子
 */
function generateFallbackId(): string {
  // タイムスタンプベースの識別子生成
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 0xffff);
  const combined = (timestamp + random).toString(16);

  return combined.substring(0, 8).toUpperCase().padStart(8, "0");
}

/**
 * お問い合わせメールの件名に識別子を追加する
 * @param {string} baseSubject - 基本の件名
 * @param {string} contactId - お問い合わせ識別子
 * @returns {string} 識別子付きの件名
 */
export function addContactIdToSubject(baseSubject: string, contactId: string): string {
  return `${baseSubject} [お問い合わせID: ${contactId}]`;
}

/**
 * お問い合わせデータの型定義
 */
export interface ContactData {
  subject: string;
  body: string;
  contactId?: string;
  userId?: string;
  timestamp?: string;
}

/**
 * お問い合わせデータを準備する
 * @param {string} baseSubject - 基本の件名
 * @param {string} body - メール本文
 * @param {string} userId - ユーザーID（オプション）
 * @returns {ContactData} 準備されたお問い合わせデータ
 */
export function prepareContactData(baseSubject: string, body: string, userId?: string): ContactData {
  const contactId = generateContactId();
  const subject = addContactIdToSubject(baseSubject, contactId);
  const timestamp = new Date().toISOString();

  return {
    subject,
    body,
    contactId,
    userId,
    timestamp,
  };
}
